/**
 * Installation / removal invoice line totals and tax (Cost Breakdown).
 */

export function computeJobProductSubtotalFromItems(items: any[]): number {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum: number, it: any) => {
    const sub = Number(it?.item_sub_total ?? it?.total_price ?? 0);
    if (sub > 0) return sum + sub;
    const qty = Number(it?.quantity ?? it?.quantity_to_be_removed ?? 1);
    const unit = Number(
      it?.changed_unit_price ?? it?.unit_price ?? it?.unit_retail_price ?? it?.unit_purchase_price ?? 0
    );
    return sum + qty * unit;
  }, 0);
}

export function computeJobProductTaxFromItems(items: any[]): number {
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (sum: number, it: any) => sum + Number(it?.item_tax_info?.total_product_tax_amount ?? it?.tax_amount ?? 0),
    0
  );
}

export function computeInvoiceJobSubtotal(
  isInstall: boolean,
  isRemoval: boolean,
  effOrder: any,
  productSubtotalFromItems: number
): number {
  if (isInstall) return Number(effOrder?.installation_cost || 0) + productSubtotalFromItems;
  if (isRemoval) return Number(effOrder?.removal_cost || 0);
  return 0;
}

export function computeInstallationRemovalTaxAmount(
  isInstall: boolean,
  isRemoval: boolean,
  effOrder: any,
  specialFeesTax: number,
  productTaxFromItems: number
): number {
  if (isInstall) {
    const saved = Number(effOrder?.over_all_tax_amount ?? effOrder?.installationtaxamount);
    if (saved > 0) return saved;
    return Number(effOrder?.installation_tax_amount || 0) + specialFeesTax + productTaxFromItems;
  }
  if (isRemoval) {
    const saved = Number(effOrder?.over_all_tax_amount);
    if (saved > 0) return saved;
    return Number(effOrder?.removal_tax_amount || 0) + specialFeesTax + productTaxFromItems;
  }
  return 0;
}

export function computeInstallationRemovalDiscountAmount(
  isInstall: boolean,
  isRemoval: boolean,
  effOrder: any,
  items: any[]
): number {
  if (!isInstall && !isRemoval) return 0;
  const rootDiscountAmount = Number(effOrder?.discount_amount || 0);
  if (rootDiscountAmount > 0) return rootDiscountAmount;
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (sum: number, it: any) => sum + Number(it?.discount_on_total_quantity ?? it?.discount ?? 0),
    0
  );
}

/** Root `discount_code` / `coupon_code`, else first discounted line item code (product-scoped coupons). */
export function resolveInstallationRemovalDiscountCode(order: any, lineItems?: any[]): string {
  const rootCode = String(order?.discount_code ?? order?.coupon_code ?? "").trim();
  if (rootCode) return rootCode;

  const items = Array.isArray(lineItems)
    ? lineItems
    : Array.isArray(order?.line_items)
      ? order.line_items
      : [];

  const lineHasDiscountCode = (li: any) =>
    Number(
      li?.discount_on_total_quantity ??
        li?.discount ??
        li?.discount_amount ??
        li?.discount_on_total_quantity_amount ??
        0
    ) > 0 && Boolean(String(li?.discount_code ?? "").trim());

  const fromNewPurchase = items.find(
    (li: any) => li?.is_new_purchase === true && lineHasDiscountCode(li)
  );
  if (fromNewPurchase) return String(fromNewPurchase.discount_code).trim();

  const fromAnyLine = items.find(lineHasDiscountCode);
  return String(fromAnyLine?.discount_code ?? "").trim();
}
